"use client";

import { useState, useEffect } from "react";
import { formatDistance } from "date-fns";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Clock,
  CheckCircle,
  XCircle,
  Play,
  FileText,
  AlertTriangle,
} from "lucide-react";
import { NotebookCell } from "@/components/notebook/NotebookCell";
import { API_BASE_URL } from "@/lib/config";
import useSWR from "swr";
import { cn } from "@/lib/utils";

const fetcher = (url: string) => fetch(url).then((res) => res.json());

interface HistoricalRunDetailsProps {
  runId: string;
  onBackToRuns: () => void;
  onBackToPlan: () => void;
  onCellsUpdate?: (cells: any[]) => void;
  onCurrentCellChange?: (cellId: string) => void;
  focusedCellId?: string;
  focusedCellAnimationKey?: number;
}

export function HistoricalRunDetails({
  runId,
  onBackToRuns,
  onBackToPlan,
  onCellsUpdate,
  onCurrentCellChange,
  focusedCellId,
  focusedCellAnimationKey = 0,
}: HistoricalRunDetailsProps) {
  const { data: runData } = useSWR(
    `${API_BASE_URL}/strategies/run-details/${runId}`,
    fetcher
  );

  // Send cells data to parent
  useEffect(() => {
    if (runData?.cells && onCellsUpdate) {
      onCellsUpdate(runData.cells);
    }
  }, [runData?.cells, onCellsUpdate]);

  if (!runData || !runData.cells) {
    return (
      <div className="flex flex-col h-full items-center justify-center">
        <div className="text-center py-8">
          <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-yellow-600" />
          <h3 className="text-lg font-semibold mb-2">Run Not Found</h3>
          <p className="text-muted-foreground mb-4">
            The requested run could not be found or may have been deleted.
          </p>
          <Button onClick={onBackToRuns} variant="outline">
            Back to Runs
          </Button>
        </div>
      </div>
    );
  }

  const formatDate = (timestamp: number) => {
    return formatDistance(new Date(timestamp * 1000), new Date(), {
      addSuffix: true,
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "success":
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case "error":
        return <XCircle className="h-5 w-5 text-red-600" />;
      case "running":
        return <Play className="h-5 w-5 text-blue-600" />;
      default:
        return <Clock className="h-5 w-5 text-gray-600" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "success":
        return (
          <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
            Success
          </Badge>
        );
      case "error":
        return <Badge variant="destructive">Error</Badge>;
      case "running":
        return (
          <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
            Running
          </Badge>
        );
      default:
        return <Badge variant="secondary">Draft</Badge>;
    }
  };

  const successfulCells = runData.cells.filter(
    (cell: any) => cell.status === "success"
  ).length;
  const failedCells = runData.cells.filter(
    (cell: any) => cell.status === "error"
  ).length;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-base font-medium">{runData.name}</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Started {formatDate(runData.createdAt)} • {runData.cells.length}{" "}
            steps total
          </p>
        </div>

        <div className="flex items-center gap-2">
          {getStatusIcon(runData.status)}
          {getStatusBadge(runData.status)}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto">
        {runData.cells.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No execution steps found for this run.</p>
          </div>
        ) : (
          <div className="space-y-2 py-0">
            {runData.cells.map((cell: any) => (
              <div
                key={cell.id}
                id={`cell-${cell.id}`}
                className={cn(
                  "rounded-xl transition-all duration-300",
                  focusedCellId === cell.id &&
                    (focusedCellAnimationKey % 2 === 0
                      ? "citation-cell-highlight-a"
                      : "citation-cell-highlight-b")
                )}
              >
                <NotebookCell
                  cell={cell}
                  isEditButtonShown={false} // Read-only mode for historical runs
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
